import { useEffect, useRef } from 'react'
import { useQueryClient } from 'react-query'
import { useAuthedSocket } from '@/hooks/useAuthedSocket'
import type { SystemIssue, SystemIssueMessage } from '../types/system-issues'
import { issueKey, myIssuesKey, refreshIssueLists } from './useSystemIssues'
import { issueMessagesKey } from './useSystemIssueMessages'
import { resolutionSummariesKey } from './useResolutionSummaries'

const SYSTEM_ISSUES_EVENTS = [
  'issue.created',
  'issue.updated',
  'message.created',
  'read.updated',
  'mention.added',
  'resolution-summary.updated',
] as const

interface UseSystemIssuesSocketOptions {
  issueId: number | null
}

/**
 * Namespace `/system-issues`, separado de `/occurrences` — mesmo esqueleto
 * de `useOccurrencesSocket.ts`, incluindo o cuidado com reconexão: o handler
 * de `connect` reemite `join_issue` a cada (re)conexão, não só na primeira
 * montagem — sem isso, um reconnect deixaria o socket fora da room da issue
 * e a thread pararia de receber updates em tempo real silenciosamente (bug
 * já caçado e corrigido em Ocorrências nesta mesma base).
 */
export function useSystemIssuesSocket({ issueId }: UseSystemIssuesSocketOptions) {
  const qc = useQueryClient()
  const { socket } = useAuthedSocket('/system-issues')
  const currentIssueRef = useRef<number | null>(null)

  useEffect(() => {
    if (!socket) return
    const onConnect = () => {
      socket.emit('system-issues.join_user')
      if (currentIssueRef.current) {
        socket.emit('system-issues.join_issue', {
          issueId: currentIssueRef.current,
        })
      }
    }
    socket.on('connect', onConnect)
    if (socket.connected) onConnect()

    // Payload escalar (entidade crua, atores não populados) — handler só
    // invalida cache por id, refetch traz o shape completo (UserRef).
    socket.on(
      'issue.created',
      (issue: Pick<SystemIssue, 'id' | 'created_by_id'>) => {
        void issue
        refreshIssueLists(qc)
      },
    )

    socket.on('issue.updated', (issue: Pick<SystemIssue, 'id'>) => {
      qc.invalidateQueries(issueKey(issue.id))
      refreshIssueLists(qc)
    })

    socket.on(
      'message.created',
      (message: Pick<SystemIssueMessage, 'id' | 'issue_id'>) => {
        qc.invalidateQueries(issueMessagesKey(message.issue_id))
        qc.invalidateQueries(issueKey(message.issue_id))
        refreshIssueLists(qc)
      },
    )

    // Emitido só pra `user:{account_id}` — mantém outras abas/dispositivos
    // do MESMO usuário em sincronia quando ele marca uma issue como lida em
    // uma delas (o badge de não-lidas some em todas, não só na que agiu).
    socket.on('read.updated', () => {
      refreshIssueLists(qc)
    })

    // Emitido só pra `user:{mentioned_account_id}` quando alguém marca esse
    // usuário numa issue — "Meus problemas" passa a incluí-la sem precisar
    // recarregar a página.
    socket.on('mention.added', (data: { issue_id: number }) => {
      qc.invalidateQueries(myIssuesKey)
      qc.invalidateQueries(issueKey(data.issue_id))
    })

    // Só a room `developers` recebe: resumo da solução criado/editado por
    // outro dev — a conversa aberta mostra o texto novo.
    socket.on('resolution-summary.updated', (data: { issue_id: number }) => {
      qc.invalidateQueries(resolutionSummariesKey(data.issue_id))
    })

    return () => {
      for (const event of SYSTEM_ISSUES_EVENTS) socket.off(event)
      socket.off('connect', onConnect)
    }
  }, [socket, qc])

  useEffect(() => {
    if (!socket) return
    const prev = currentIssueRef.current
    if (prev && prev !== issueId) {
      socket.emit('system-issues.leave_issue', { issueId: prev })
    }
    if (issueId) {
      socket.emit('system-issues.join_issue', { issueId })
    }
    currentIssueRef.current = issueId
  }, [issueId, socket])
}
